import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../AuthContext";
import "./AuthModal.css";

const VIEWS = {
  LOGIN: "login",
  REGISTER: "register",
  RESET: "reset",
};

export default function AuthModal({ onClose, onGoogleLoginSuccess, initialView }) {
  const { t } = useTranslation();
  const { login, register, loginWithGoogle, resetPassword } = useAuth();
  // Na které záložce se okno otevře. Výchozí je přihlášení a zůstává jím
  // všude, odkud se otevírá bez upřesnění; brána u megaslova si od 14. 9. 2026
  // říká o konkrétní, protože z ní vedou dvě tlačítka a každé jinam.
  const [view, setView] = useState(
    initialView === VIEWS.REGISTER ? VIEWS.REGISTER : VIEWS.LOGIN
  );
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  function clearMessages() {
    setError("");
    setMessage("");
  }

  async function handleLogin(e) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await login(email, password);
      onClose();
    } catch (err) {
      setError(friendlyError(err.code, "prihlaseni"));
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister(e) {
    e.preventDefault();
    clearMessages();
    if (!displayName.trim()) {
      setError(t("auth.enterNickname"));
      return;
    }
    setLoading(true);
    try {
      await register(email, password, displayName.trim());
      setMessage(t("auth.messages.registrationSuccess"));
      setTimeout(onClose, 3000);
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    clearMessages();
    setLoading(true);
    try {
      const credential = await loginWithGoogle();
      if (onGoogleLoginSuccess) {
        onGoogleLoginSuccess(credential?.user || null);
      }
      onClose();
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  async function handleReset(e) {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await resetPassword(email);
      setMessage(t("auth.messages.passwordResetSent"));
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  // `kontext` je tu kvůli jedinému kódu. `auth/too-many-requests` znamená v
  // každém ze čtyř toků něco jiného, a hláška o dočasném zámku přihlašování
  // dává smysl jen v tom prvním. Při registraci se žádné heslo nezkoušelo, u
  // Googlu žádné neexistuje a při žádosti o nové heslo by ta věta posílala
  // člověka udělat přesně to, co právě dělá. Zbylé tři toky proto dostanou
  // obyčejné „moc rychle za sebou“.
  function friendlyError(code, kontext) {
    const map = {
      "auth/invalid-email": t("auth.errors.invalidEmail"),
      "auth/user-not-found": t("auth.errors.userNotFound"),
      "auth/wrong-password": t("auth.errors.wrongPassword"),
      "auth/invalid-credential": t("auth.errors.invalidCredential"),
      "auth/email-already-in-use": t("auth.errors.emailInUse"),
      "auth/weak-password": t("auth.errors.weakPassword"),
      "auth/too-many-requests": t("auth.errors.tooManyRequests"),
      // Three ways the Google sign-in window fails that are NOT the player
      // changing their mind, and until 2026-09-01 two of them fell through to
      // "something went wrong". A player wrote that day describing sign-in that
      // works morning and evening and not in between — the kind of report that
      // is only answerable if the message names what actually happened.
      //
      // popup-closed-by-user is the one that was already here, and its old
      // wording ("Prihlaseni bylo zruseno") blamed her for it. On mobile Safari
      // the browser closes that window on its own, so the message says what to
      // do next instead of who did it.
      "auth/popup-closed-by-user": t("auth.errors.popupClosed"),
      "auth/popup-blocked": t("auth.errors.popupBlocked"),
      "auth/cancelled-popup-request": t("auth.errors.popupCancelled"),
      // Safari with cross-site tracking prevention, and private windows: Firebase
      // cannot reach the storage it signs in through. Nothing the player can
      // retry their way out of, so the message points at the setting.
      "auth/web-storage-unsupported": t("auth.errors.storageBlocked"),
      "auth/network-request-failed": t("auth.errors.networkError"),
      // Doména není v seznamu autorizovaných ve Firebase. Není to chyba hráče a
      // nedá se přečkat opakováním — dokud ji tam někdo nepřidá, přihlášení přes
      // Google na té doméně nepůjde vůbec.
      //
      // Stálo to spuštění slovenské verze 11. 9. 2026: prihorieva.sk v seznamu
      // nebylo, kdežto sk-test-dot-prihoriva.ew.r.appspot.com ano, takže na
      // stagingu se to chovalo správně a na ostré doméně ne. Hráč viděl jen
      // „Nastala chyba. Skúste to znova.“, což ho poslalo zkoušet znovu něco, co
      // nemohlo začít fungovat.
      "auth/unauthorized-domain": t("auth.errors.unauthorizedDomain"),
    };
    if (code === "auth/too-many-requests" && kontext !== "prihlaseni") {
      return t("auth.errors.tooManyRequestsGeneric");
    }
    return map[code] || t("auth.errors.generic");
  }

  return (
    <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose} aria-label={t("buttons.close")}>✕</button>

        {/* Tabs (login / register) */}
        {view !== VIEWS.RESET && (
          <div className="auth-tabs">
            <button
              className={view === VIEWS.LOGIN ? "auth-tab active" : "auth-tab"}
              onClick={() => { setView(VIEWS.LOGIN); clearMessages(); }}
            >
              {t("auth.tabLogin")}
            </button>
            <button
              className={view === VIEWS.REGISTER ? "auth-tab active" : "auth-tab"}
              onClick={() => { setView(VIEWS.REGISTER); clearMessages(); }}
            >
              {t("auth.tabRegister")}
            </button>
          </div>
        )}

        {/* Feedback messages */}
        {error && <p className="auth-error">{error}</p>}
        {message && <p className="auth-success">{message}</p>}

        {/* ── Login ── */}
        {view === VIEWS.LOGIN && (
          <form onSubmit={handleLogin} className="auth-form">
            <input
              type="email"
              placeholder={t("auth.placeholder.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder={t("auth.placeholder.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? t("auth.loading.login") : t("buttons.login")}
            </button>
            <button
              type="button"
              className="auth-btn google"
              onClick={handleGoogle}
              disabled={loading}
            >
              <span className="google-icon">G</span> {t("buttons.loginGoogle")}
            </button>
            <button
              type="button"
              className="auth-link"
              onClick={() => { setView(VIEWS.RESET); clearMessages(); }}
            >
              {t("buttons.forgotPassword")}
            </button>
          </form>
        )}

        {/* ── Register ── */}
        {view === VIEWS.REGISTER && (
          <form onSubmit={handleRegister} className="auth-form">
            <input
              type="text"
              placeholder={t("auth.placeholder.nickname")}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              maxLength={30}
              autoComplete="nickname"
            />
            <input
              type="email"
              placeholder={t("auth.placeholder.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <input
              type="password"
              placeholder={t("auth.placeholder.passwordMin")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? t("auth.loading.register") : t("buttons.register")}
            </button>
            <button
              type="button"
              className="auth-btn google"
              onClick={handleGoogle}
              disabled={loading}
            >
              <span className="google-icon">G</span> {t("buttons.registerGoogle")}
            </button>
          </form>
        )}

        {/* ── Reset password ── */}
        {view === VIEWS.RESET && (
          <form onSubmit={handleReset} className="auth-form">
            <h3 className="auth-reset-title">{t("auth.resetTitle")}</h3>
            <input
              type="email"
              placeholder={t("auth.placeholder.yourEmail")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <button type="submit" className="auth-btn primary" disabled={loading}>
              {loading ? t("auth.loading.sending") : t("buttons.sendReset")}
            </button>
            <button
              type="button"
              className="auth-link"
              onClick={() => { setView(VIEWS.LOGIN); clearMessages(); }}
            >
              {t("buttons.backToLogin")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
